/**
 * Centralized Test IDs Registry
 *
 * Single source of truth for all data-testid attributes used in the application.
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  MANDATORY: All test IDs MUST be defined in this file before use.         ║
 * ║  Using a test ID not defined here is a violation of project conventions.  ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * This ensures:
 * - Consistency between components and tests
 * - No typos (caught at compile time)
 * - Autocomplete support in IDEs
 * - Single place to audit all testable elements
 * - Easy refactoring when IDs need to change
 *
 * ## Naming Conventions
 *
 * - Use kebab-case for all IDs: `contact-card`, not `contactCard`
 * - Prefix with feature/domain: `contacts-list`, `packets-grid`
 * - Action buttons: `{domain}-{action}-button` (e.g., `contacts-delete-button`)
 * - Forms: `{domain}-{field}-input` (e.g., `auth-email-input`)
 * - Dynamic IDs use functions: `contacts.card(123)` → `contact-card-123`
 *
 * ## Usage
 *
 * In components:
 * ```tsx
 * import { testIds } from '@/test/testIds';
 * <Button data-testid={testIds.contacts.deleteButton}>Delete</Button>
 * <Card data-testid={testIds.contacts.card(contact.id)}>...</Card>
 * ```
 *
 * In tests:
 * ```tsx
 * import { testIds } from '@/test/testIds';
 * screen.getByTestId(testIds.contacts.deleteButton);
 * screen.getByTestId(testIds.contacts.card(123));
 * ```
 *
 * ## Adding New Test IDs
 *
 * 1. Add the ID to the appropriate section in this file
 * 2. Use the established naming pattern for that domain
 * 3. For dynamic IDs, create a function that returns a template literal
 * 4. Export via the main `testIds` object
 */

// ============================================
// Auth / Accounts
// ============================================

export const authTestIds = {
  // Sign In
  signInForm: 'auth-sign-in-form',
  signInEmailInput: 'auth-sign-in-email-input',
  signInPasswordInput: 'auth-sign-in-password-input',
  signInSubmitButton: 'auth-sign-in-submit-button',
  signInGoogleButton: 'auth-sign-in-google-button',
  signInMicrosoftButton: 'auth-sign-in-microsoft-button',
  signInBackButton: 'auth-back-button',
  signInUsePasswordButton: 'auth-use-password-button',
  signInUseMagicLinkButton: 'auth-use-magic-link-button',
  /** vingreact#106 — the `?error=` alert on the sign-in card. */
  signInLinkFailureAlert: 'auth-sign-in-link-failure-alert',
  /** #268 — the `?reason=` (session bounce) alert on the sign-in card. */
  signInBounceAlert: 'auth-sign-in-bounce-alert',

  // Sign Up
  signUpForm: 'auth-sign-up-form',
  signUpEmailInput: 'auth-sign-up-email-input',
  signUpPasswordInput: 'auth-sign-up-password-input',
  signUpConfirmPasswordInput: 'auth-sign-up-confirm-password-input',
  signUpSubmitButton: 'auth-sign-up-submit-button',
  signUpGoogleButton: 'auth-sign-up-google-button',
  signUpMicrosoftButton: 'auth-sign-up-microsoft-button',
  signUpAdpButton: 'auth-sign-up-adp-button',
  signUpTurnstile: 'auth-sign-up-turnstile',

  // Forgot Password
  forgotPasswordForm: 'auth-forgot-password-form',
  forgotPasswordEmailInput: 'auth-forgot-password-email-input',
  forgotPasswordSubmitButton: 'auth-forgot-password-submit-button',
  forgotPasswordGoogleButton: 'auth-forgot-password-google-button',
  forgotPasswordMicrosoftButton: 'auth-forgot-password-microsoft-button',
  forgotPasswordAdpButton: 'auth-forgot-password-adp-button',
  forgotPasswordBackButton: 'auth-forgot-password-back-button',

  // SSO
  ssoForm: 'auth-sso-form',
  ssoEmailInput: 'auth-sso-email-input',
  ssoSubmitButton: 'auth-sso-submit-button',
  ssoBackButton: 'auth-sso-back-button',
  ssoFailureTitle: 'auth-sso-failure-title',
  ssoFailureGuidance: 'auth-sso-failure-guidance',
  ssoHandoffLogo: 'auth-sso-handoff-logo',

  // OAuth buttons (alternative names) — F11: each must have a UNIQUE
  // value, otherwise two distinct keys point at the same DOM testid and
  // queries can't disambiguate. The "alternative name" keys are kept as
  // distinct testids so callers using either spelling get a distinct
  // assertion target.
  googleSignInButton: 'auth-google-sign-in-button',
  adpSignInButton: 'auth-adp-sign-in-button',
  noviSignInButton: 'auth-novi-sign-in-button',
} as const;

// ============================================
// Contacts
// ============================================

export const contactsTestIds = {
  // List view
  list: 'contacts-list',
  grid: 'contacts-grid',
  searchInput: 'contacts-search-input',
  filterSidebar: 'contacts-filter-sidebar',

  // Cards
  card: (id: number | string) => `contact-card-${id}` as const,
  cardCheckbox: (id: number | string) => `contact-card-${id}-checkbox` as const,
  cardAvatar: (id: number | string) => `contact-card-${id}-avatar` as const,

  // Header actions
  headerActionButton: 'contacts-header-action-button',
  createButton: 'contacts-create-button',
  importButton: 'contacts-import-button',
  exportButton: 'contacts-export-button',
  deleteButton: 'contacts-delete-button',
  archiveButton: 'contacts-archive-button',
  unarchiveButton: 'contacts-unarchive-button',
  changeOwnerButton: 'contacts-change-owner-button',
  assignLabelsButton: 'contacts-assign-labels-button',
  mergeButton: 'contacts-merge-button',
  loginLinkButton: 'contacts-login-link-button',
  filterButton: 'contacts-filter-button',

  // Detail view
  detailPanel: 'contact-detail-panel',
  detailName: 'contact-detail-name',
  detailTitle: 'contact-detail-title',
  detailAvatar: 'contact-detail-avatar',
  detailTab: (tab: string) => `contact-detail-tab-${tab}` as const,
  detailCancelButton: 'contact-detail-cancel-button',
  detailConfirmButton: 'contact-detail-confirm-button',
  detailSetUserConfirmButton: 'contact-detail-set-user-confirm-button',

  // Contact methods list
  methodsList: 'contact-methods-list',
  methodItem: (index: number) => `contact-method-${index}` as const,
  addMethodButton: 'contact-add-method-button',
  confirmAddMethodButton: 'contact-confirm-add-method-button',
  methodRetryButton: (index: number) => `contact-method-${index}-retry` as const,
  methodActivateButton: (index: number) => `contact-method-${index}-activate` as const,
  methodDeactivateButton: (index: number) => `contact-method-${index}-deactivate` as const,
  /** #1220: per ADDRESS (slug like badReason): an active and a bad row both carry Remove, and idx repeats across row kinds. */
  methodRemoveButton: (method: string) => `contact-method-remove-${method.replace(/[^a-z0-9]/gi, '-').toLowerCase()}` as const,

  // Inline date field
  dateClearButton: 'contact-date-clear-button',

  // Attributes
  addAttributeButton: 'contact-add-attribute-button',
  newAttributeNameInput: 'contact-new-attribute-name',
  attributeClearButton: 'contact-attribute-clear-button',

  // Labels editor — inline add/remove, no separate manage-labels dialog
  labelsEditor: 'contact-labels-editor',
  labelsAddInput: 'contact-labels-add-input',
  labelsCreateChip: 'contact-labels-create-chip',
  /** Suggestion chip for an existing label — smoke: contacts.data (L1, #197) */
  labelsSuggestionChip: (name: string) =>
    `contact-labels-suggestion-${name}` as const,
  // F11: preserve the original casing and exact characters of the user-
  // supplied name so two distinct labels can't collide on testid. Any
  // character outside the [-\w.] safe set is hex-escaped; the original
  // case is kept so "Foo Bar" and "foo-bar" map to different testids.
  labelChip: (name: string) =>
    `contact-label-${name.replace(/[^\w.-]/g, (c) => `_${c.charCodeAt(0).toString(16)}_`)}` as const,
  labelRemoveButton: (name: string) =>
    `contact-label-remove-${name.replace(/[^\w.-]/g, (c) => `_${c.charCodeAt(0).toString(16)}_`)}` as const,

  /** The contact card the Contacts list draws (also the held-by-linked dialog's holder card). */
  contactCard: (id: number | string) => `contact-card-${id}` as const,
  /** The card's red '!' (shown iff the server says `reachable === false`). */
  contactCardUnreachable: (id: number | string) => `contact-card-unreachable-${id}` as const,
  contactCardCaption: (id: number | string) => `contact-card-caption-${id}` as const,
  contactCardCaptionReason: (id: number | string) => `contact-card-caption-reason-${id}` as const,
  contactCardCaptionDay: (id: number | string) => `contact-card-caption-day-${id}` as const,

  // held_by_linked_contact (#223 P1, #234 decisions) prompt — create and edit
  // share it. One holder per dialog, one footer decision; the inside and the
  // crossing variants offer different pairs, so each button has its own id.
  heldByLinkedDialog: 'contacts-held-by-linked-dialog',
  heldByLinkedCancelButton: 'contacts-held-by-linked-cancel-button',
  /** Inside: re-point the address onto the contact being created/edited. */
  heldByLinkedMoveButton: 'contacts-held-by-linked-move-button',
  /** Inside: merge the two contacts (edit) / open the holder (create). */
  heldByLinkedMergeButton: 'contacts-held-by-linked-merge-button',
  /** Crossing: "Yes, same person". */
  heldByLinkedSamePersonButton: 'contacts-held-by-linked-same-person-button',
  /** Crossing: "No, different person". */
  heldByLinkedDifferentPersonButton: 'contacts-held-by-linked-different-person-button',

  // address_on_unmanaged_contact (#1813, vingapi#979): one small dialog, one OK.
  unmanagedAddressDialog: 'unmanaged-address-dialog',
  unmanagedAddressOk: 'unmanaged-address-ok',

  // Source (ADP / Manual)
  contactSource: 'contact-source',

  // Create dialog
  createDialog: 'contacts-create-dialog',
  createDialogAddMethodButton: 'contacts-create-add-method-button',
  createDialogRemoveMethodButton: (index: number) => `contacts-create-remove-method-${index}` as const,
  /** #807: the server's refusal under one added method row. */
  createDialogMethodError: (index: number) => `contacts-create-method-error-${index}` as const,

  // Upload dialog
  uploadDialog: 'contacts-upload-dialog',
  uploadDialogBrowseButton: 'contacts-upload-browse-button',
  uploadDialogRemoveFileButton: 'contacts-upload-remove-file-button',
  uploadDialogCloseButton: 'contacts-upload-close-button',
  uploadDialogResetButton: 'contacts-upload-reset-button',

  // Merge dialog
  mergeDialog: 'contacts-merge-dialog',
  mergeDialogCloseButton: 'contacts-merge-close-button',
  /** #1959-b: the server's confirm lines under the cards (role=status), and one per person. */
  mergeDialogConfirmLines: 'merge-dialog-confirm-lines',
  mergeDialogConfirmLine: (userId: number) => `merge-dialog-confirm-line-${userId}` as const,
} as const;

// ============================================
// Packets
// ============================================

export const packetsTestIds = {
  // List view
  list: 'packets-list',
  grid: 'packets-grid',
  searchInput: 'packets-search-input',
  filterSidebar: 'packets-filter-sidebar',

  // Cards
  card: (id: number | string) => `packet-card-${id}` as const,
  cardCheckbox: (id: number | string) => `packet-card-${id}-checkbox` as const,
  cardThumbnail: (id: number | string) => `packet-card-${id}-thumbnail` as const,

  // Actions
  createButton: 'packets-create-button',
  deleteButton: 'packets-delete-button',
  archiveButton: 'packets-archive-button',
  duplicateButton: 'packets-duplicate-button',
  shareButton: 'packets-share-button',
  changeOwnerButton: 'packets-change-owner-button',

  // Detail view
  detailPanel: 'packet-detail-panel',
  detailTitle: 'packet-detail-title',
  detailSummary: 'packet-detail-summary',
  detailTab: (tab: string) => `packet-detail-tab-${tab}` as const,

  // History revision entries (#663b)
  revisionEntry: (id: number | string) => `revision-entry-${id}` as const,

  // Editor
  editor: 'packet-editor',
  editorTitle: 'packet-editor-title',
  editorSummary: 'packet-editor-summary',
  editorBackButton: 'packet-editor-back-button',
  editorDeleteButton: 'packet-editor-delete-button',
  editorDeleteConfirmButton: 'packet-editor-delete-confirm-button',
  componentList: 'packet-component-list',
  componentItem: (id: number | string) => `packet-component-${id}` as const,
  addComponentButton: 'packet-add-component-button',

  // Component editor
  componentEditor: 'packet-component-editor',
  componentTitle: 'packet-component-title',
  componentTypeSelector: 'packet-component-type-selector',
  componentShowSummaryButton: 'packet-component-show-summary-button',
  componentSummaryInput: 'packet-component-summary-input',

  // Scheduled invitations
  scheduledList: 'packet-scheduled-list',
  scheduledItem: (id: number | string) => `packet-scheduled-${id}` as const,
  addScheduledButton: 'packet-add-scheduled-button',
  scheduledEditButton: (id: number | string) => `packet-scheduled-${id}-edit` as const,
  scheduledRescheduleButton: (id: number | string) => `packet-scheduled-${id}-reschedule` as const,
  scheduledDeleteButton: (id: number | string) => `packet-scheduled-${id}-delete` as const,
  scheduledSendingIcon: (id: number | string) => `packet-scheduled-${id}-sending-icon` as const,
  scheduledRecurringIcon: (id: number | string) => `packet-scheduled-${id}-recurring-icon` as const,
  /** #1549-a: line 2 of a row whose count is the warning ("No recipients" / "n contacts (was m)"). */
  scheduledCountWarning: (id: number | string) => `packet-scheduled-${id}-count-warning` as const,
  /** #875a: line 2 of a row the send task is processing (`resolving`). */
  scheduledCheckingRecipients: (id: number | string) => `packet-scheduled-${id}-checking-recipients` as const,
  scheduledDeleteDialogCancelButton: 'packet-scheduled-delete-cancel-button',
  scheduledRescheduleDialogCancelButton: 'packet-scheduled-reschedule-cancel-button',
} as const;

// ============================================
// Checklists
// ============================================

export const checklistsTestIds = {
  // List view
  list: 'checklists-list',
  grid: 'checklists-grid',
  searchInput: 'checklists-search-input',
  filterSidebar: 'checklists-filter-sidebar',

  // Cards
  card: (id: number | string) => `checklist-card-${id}` as const,
  cardCheckbox: (id: number | string) => `checklist-card-${id}-checkbox` as const,

  // Actions
  createButton: 'checklists-create-button',
  deleteButton: 'checklists-delete-button',
  archiveButton: 'checklists-archive-button',
  duplicateButton: 'checklists-duplicate-button',
  shareButton: 'checklists-share-button',

  // Detail view
  detailPanel: 'checklist-detail-panel',
  detailTitle: 'checklist-detail-title',
  detailTab: (tab: string) => `checklist-detail-tab-${tab}` as const,

  // Editor (main container)
  editor: 'checklist-editor',
  editorBackButton: 'checklist-editor-back-button',
  editorCancelButton: 'checklist-editor-cancel-button',
  editorPublishButton: 'checklist-editor-publish-button',

  // Question list (left pane)
  questionListContainer: 'checklist-question-list-container',
  questionList: 'checklist-question-list',
  titleInput: 'checklist-title-input',
  summaryInput: 'checklist-summary-input',
  questionItem: (index: number) => `checklist-question-${index}` as const,
  questionItemDelete: (index: number) => `checklist-question-${index}-delete` as const,
  addQuestionButton: 'checklist-add-question-button',

  // Add question panel (right pane when no question selected)
  addQuestionPanel: 'checklist-add-question-panel',
  questionTypeCard: (type: string) => `checklist-question-type-${type}` as const,

  // Question editor (right pane when question selected)
  questionEditor: 'checklist-question-editor',
  questionEditorCloseButton: 'checklist-question-editor-close-button',
  questionText: 'checklist-question-text',
  questionTypeSelector: 'checklist-question-type-selector',
  questionImageUploadButton: 'checklist-question-image-upload-button',
  choiceItem: (index: number) => `checklist-choice-${index}` as const,
  choiceImageUploadButton: (index: number) => `checklist-choice-${index}-image-upload` as const,
  addChoiceButton: 'checklist-add-choice-button',

  // Submissions
  submissionsList: 'checklist-submissions-list',
  submissionItem: (id: number | string) => `checklist-submission-${id}` as const,
  resolveButton: 'checklist-resolve-button',
  resolveDialog: 'checklist-resolve-dialog',
  resolveDialogSelectAllButton: 'checklist-resolve-select-all-button',
} as const;

// ============================================
// Consume (Viewer)
// ============================================

export const consumeTestIds = {
  // Packet viewer
  packetViewer: 'consume-packet-viewer',
  menuButton: 'consume-menu-button',
  prevButton: 'consume-prev-button',
  nextButton: 'consume-next-button',
  completeButton: 'consume-complete-button',
  progressBar: 'consume-progress-bar',
  // Sign-in affordance on the save-failure banner: shown when a write was
  // refused because the viewer is signed out (#133). Their unsaved work is
  // held until they take it.
  saveSignInButton: 'consume-save-sign-in-button',
  // #989 S4u: the labeled Reload control on the "changed elsewhere" banner
  // (another window or device saved answers after this page). Same shared banner button
  // as the sign-in control (SaveBannerButton).
  saveReloadButton: 'consume-save-reload-button',

  // Component display
  componentDisplay: 'consume-component-display',
  // The titlebar's centre cell: the selected component's title. The live
  // smoke's Learner.advanceTo reads it (vingreact#711).
  componentTitle: 'consume-component-title',
  // The media elements themselves. A live test drives real playback through
  // them (play/rate/seek) and reads `duration`/`played`, so they need a stable
  // handle — matching `audio`/`video` by tag would break the moment a wrapper
  // player is introduced.
  audioPlayer: 'consume-audio-player',
  videoPlayer: 'consume-video-player',
  mediaUnsupported: 'consume-media-unsupported',
  lightboxCloseButton: 'consume-lightbox-close-button',
  linkClickButton: 'consume-link-click-button',
  attachmentClickButton: 'consume-attachment-click-button',
  fileUploadButton: 'consume-file-upload-button',

  // File upload response
  fileUploadRemoveButton: 'consume-file-upload-remove-button',

  // Question display
  questionDisplay: 'consume-question-display',
  /** #989 S7t: a question's card root; carries `data-question-id`. */
  questionCard: 'consume-question-card',
  /** #989 S7t: a radio choice (the radio itself; its `value` is the choice id). */
  choice: 'consume-choice',
  /** #989 S7t: the short-answer textarea. */
  shortAnswer: 'consume-short-answer',
  questionHelpText: 'consume-question-help-text',
  questionOptionalLabel: 'consume-question-optional-label',
  questionNumber: (num: number) => `consume-question-${num}` as const,
  questionFileUploadButton: 'consume-question-file-upload-button',
  questionUploadButton: (mediaType: string) => `consume-question-upload-${mediaType}` as const,
  /** Marker on a question the server's 409 required_incomplete listed (plan R3). */
  questionIncomplete: (questionId: number) => `consume-question-incomplete-${questionId}` as const,
  certificateDropZone: 'consume-certificate-drop-zone',
  submitButton: 'consume-submit-button',

  // Access denied — verify-method offer
  verifyOfferPanel: 'consume-verify-offer-panel',
  verifyOfferSendButton: 'consume-verify-offer-send-button',
  verifyOfferResendButton: 'consume-verify-offer-resend-button',
  verifyOfferCodeInput: 'consume-verify-offer-code-input',
  verifyOfferSubmitButton: 'consume-verify-offer-submit-button',

  // Offline / transport affordances (W1 seeds; W3/W4 consume)
  offlineBanner: 'consume-offline-banner',
  /**
   * #989 S7t: the save banner per `ConsumeSaveBannerVariant` (the `offline`
   * variant keeps `offlineBanner`). `silent` renders nothing; its id exists so
   * every variant carries one.
   */
  saveBanner: {
    terminal: 'consume-save-banner-terminal',
    signIn: 'consume-save-banner-signIn',
    silent: 'consume-save-banner-silent',
    /** #989 S4u: "changed elsewhere" (a component frozen; sticky until Reload). */
    superseded: 'consume-save-banner-superseded',
    /** #1029: the page's consume row changed; the page is frozen until Reload. */
    keyMismatch: 'consume-save-banner-keyMismatch',
  },
  /** #1574: the packet and checklist viewers' "Not saved yet" banner (from 15 s unsaved) and its quiet Try again. */
  notSavedBanner: 'consume-not-saved-banner',
  notSavedRetry: 'consume-not-saved-retry',
  loadRetry: 'consume-load-retry',
  loadingPage: 'consume-loading-page',
  pdfRetry: 'consume-pdf-retry',
  fileUrlReload: 'consume-file-url-reload',

  // Checklist viewer
  checklistViewer: 'consume-checklist-viewer',

  // Checklist Submitted banner (#1110c). Its control (Go to Dashboard, signed in
  // only; #1307) carries the primary id. The staging
  // harness (checklistSubmitter.ts, checklists.spec.ts) reads the banner id;
  // completion-banner-testids.guard.test.ts pins the values.
  checklistSubmittedBanner: 'consume-checklist-submitted-banner',
  checklistSubmittedPrimaryButton: 'consume-checklist-submitted-primary-button',

  // Completion banner (#1110b). The banner carries `data-state` = complete |
  // session | skipped. Its control (Go to Dashboard / Go to Skipped)
  // carries the primary id. The staging harness (learner.ts) reads the
  // banner; completion-banner-testids.guard.test.ts pins the values.
  completionBanner: 'consume-completion-banner',
  completionPrimaryButton: 'consume-completion-primary-button',

  // View container
  viewContainerHomeButton: 'consume-view-container-home-button',

  // Access denied
  accessDeniedPage: 'consume-access-denied',
} as const;

// ============================================
// Dashboard
// ============================================

export const dashboardTestIds = {
  // Main dashboard
  container: 'dashboard-container',
  welcomeMessage: 'dashboard-welcome',

  // Charts
  assignmentsChart: 'dashboard-assignments-chart',
  submissionsChart: 'dashboard-submissions-chart',
  /** The row of three chart cards (#1561 B4). */
  chartGrid: 'dashboard-chart-grid',
  /** The row of three Recently panels. */
  recentRow: 'dashboard-recent-row',
  /** Invisible box under the Recently headers: the row's spinner is centred on it. */
  recentSpinnerAnchor: 'dashboard-recent-spinner-anchor',

  // Sections
  todoSection: 'dashboard-todo-section',
  completedSection: 'dashboard-completed-section',
  invitedSection: 'dashboard-invited-section',
  submittedSection: 'dashboard-submitted-section',

  // Items
  assignmentItem: (id: number | string) => `dashboard-assignment-${id}` as const,
  assignmentMessagesButton: (id: number | string) => `dashboard-assignment-${id}-messages` as const,
  checklistItem: (id: number | string) => `dashboard-checklist-${id}` as const,
} as const;

// ============================================
// Profile
// ============================================

export const profileTestIds = {
  // Profile menu
  menu: 'profile-menu',
  logoutButton: 'profile-logout-button',

  // Edit dialog
  editDialog: 'profile-edit-dialog',
  editDialogCloseButton: 'profile-edit-dialog-close-button',
  nameInput: 'profile-name-input',
  firstNameInput: 'profile-first-name-input',
  lastNameInput: 'profile-last-name-input',
  avatarEditor: 'profile-avatar-editor',
  saveButton: 'profile-save-button',

  // Contact methods in profile
  addContactMethodInput: 'profile-add-contact-method-input',
  addContactMethodButton: 'profile-add-contact-method-button',
  deleteContactMethodButton: (index: number) => `profile-delete-contact-method-${index}` as const,
  cancelPendingMethodButton: (index: number) => `profile-cancel-pending-method-${index}` as const,
  resendCodeButton: (index: number) => `profile-resend-code-${index}` as const,

  // Organization (legacy - being replaced by context switcher)
  orgDialog: 'profile-org-dialog',
  orgLogo: 'profile-org-logo',
  orgName: 'profile-org-name',
} as const;

// ============================================
// Context Switching
// ============================================

export const contextSwitcherTestIds = {
  list: 'context-switcher-list',
  searchInput: 'context-switcher-search',
  orgGroup: 'context-switcher-org-group',
  networkGroup: 'context-switcher-network-group',
  item: (kind: 'org' | 'network', id: number) => `context-switcher-${kind}-${id}` as const,
  loadingState: 'context-switcher-loading',
  emptyState: 'context-switcher-empty',
  errorState: 'context-switcher-error',
  switchError: 'context-switcher-switch-error',
  scrollArea: 'context-switcher-scroll-area',
} as const;

export const contextSettingsTestIds = {
  dialog: 'context-settings-dialog',
  name: 'context-settings-name',
  identifier: 'context-settings-identifier',
  switchSection: 'context-settings-switch-section',
  switchToggle: 'context-settings-switch-toggle',
  // The header logo + Novi SSO icon now use the shared AvatarEditor (its own
  // test ids); only the section wrapper keeps a dedicated id.
  headerLogoSection: 'context-settings-header-logo-section',
  badgeLogoSection: 'context-settings-badge-logo-section',
  // NOVI SSO config (network admin)
  noviSsoSection: 'context-settings-novi-sso-section',
  noviSsoButtonPreview: 'context-settings-novi-sso-button-preview',
  noviSsoEnabledToggle: 'context-settings-novi-sso-enabled',
  noviSsoBaseUrlInput: 'context-settings-novi-sso-base-url',
  noviSsoClientIdInput: 'context-settings-novi-sso-client-id',
  noviSsoClientSecretInput: 'context-settings-novi-sso-client-secret',
  noviSsoLabelInput: 'context-settings-novi-sso-label',
  noviSsoSaveButton: 'context-settings-novi-sso-save-button',
  /** The saved button icon is uploaded but not rendered yet. */
  noviSsoIconProcessing: 'context-settings-novi-sso-icon-processing',
  /** The saved button icon failed processing. */
  noviSsoIconFailed: 'context-settings-novi-sso-icon-failed',
} as const;

export const contextRequiredTestIds = {
  page: 'context-required-page',
  title: 'context-required-title',
  description: 'context-required-description',
  // The chooser itself is the shared ContextSwitcherList — use
  // `contextSwitcher` ids for the list, search and rows.
} as const;

export const forcedSelectionTestIds = {
  modal: 'forced-context-selection-modal',
  title: 'forced-context-selection-title',
  description: 'forced-context-selection-description',
  // Options/search/switch live in the shared ContextSwitcherList — use
  // `contextSwitcher` ids for those. Only the outer shell keeps dedicated ids.
} as const;

export const ctxNoAccessTestIds = {
  page: 'ctx-no-access-page',
  title: 'ctx-no-access-title',
  retry: 'ctx-no-access-retry',
  home: 'ctx-no-access-home',
} as const;

// ============================================
// Common Dialogs
// ============================================

export const dialogTestIds = {
  // Generic dialog parts
  container: (name: string) => `dialog-${name}` as const,
  title: (name: string) => `dialog-${name}-title` as const,
  content: (name: string) => `dialog-${name}-content` as const,
  closeButton: (name: string) => `dialog-${name}-close-button` as const,
  confirmButton: (name: string) => `dialog-${name}-confirm-button` as const,
  cancelButton: (name: string) => `dialog-${name}-cancel-button` as const,

  // Dialog header component
  headerCloseButton: 'dialog-header-close-button',

  // StyledDialog backdrop. Carries data-nested when the dialog is stacked on
  // another one (and so paints no scrim of its own).
  overlay: 'dialog-overlay',

  // StyledDialog error band (the `error` prop slot, flush below the footer)
  errorBand: 'dialog-error-band',

  // StyledDialog feedback band — messages routed to this dialog from a
  // descendant's `useFeedbackZone().push` (see DialogLayerContext).
  feedbackBand: 'dialog-feedback-band',
  feedbackItem: (id: string) => `dialog-feedback-item-${id}` as const,
  feedbackItemMessage: 'dialog-feedback-item-message',

  // Manage tree dialogs (#1552): the slot the tree portals its footer (Done,
  // or the delete confirm) into. `display: contents`; its parent is the footer.
  manageTreeFooter: 'dialog-manage-tree-footer',

  // Set Password dialog (#529 password prompt on sign-in arrival)
  setPassword: 'dialog-set-password',

  // Specific dialogs
  confirm: 'dialog-confirm',
  error: 'dialog-error',
  errorIcon: 'dialog-error-icon',
  errorMessage: 'dialog-error-message',
  errorDetails: 'dialog-error-details',
  serverError: 'dialog-server-error',
  serverErrorTraceId: 'dialog-server-error-trace-id',
  serverErrorCopyTrace: 'dialog-server-error-copy-trace',
  info: 'dialog-info',
  infoIcon: 'dialog-info-icon',
  infoText: 'dialog-info-text',
  infoLink: 'dialog-info-link',
  acknowledge: 'dialog-acknowledge',
  acknowledgeIcon: 'dialog-acknowledge-icon',
  acknowledgeMessage: 'dialog-acknowledge-message',
  settings: 'dialog-settings',
  settingsCloseButton: 'dialog-settings-close-button',
  settingsReadOnlyChip: 'dialog-settings-read-only-chip',

  // Share dialog
  share: 'dialog-share',
  shareCloseButton: 'dialog-share-close-button',
  shareDatePickerButton: 'dialog-share-date-picker-button',
  shareScheduleButton: 'dialog-share-schedule-button',
  shareClearScheduledDateButton: 'dialog-share-clear-scheduled-date-button',
  shareExpandButton: 'dialog-share-expand-button',
  shareRecipientPicker: 'dialog-share-recipient-picker',
  shareRecipientChip: (id: string) => `dialog-share-recipient-${id}` as const,
  shareSendButton: 'dialog-share-send-button',
  /** Cancel text button left of Send Now (1579-a) */
  shareCancelButton: 'dialog-share-cancel-button',
  /** Send-as select trigger — staff-only sender picker (#395, vingapi#771) */
  shareSendAsSelect: 'dialog-share-send-as-select',
  /** Per-option test ID for the send-as select */
  shareSendAsOption: (userId: number) => `dialog-share-send-as-option-${userId}` as const,
  /** Message textarea (create and edit mode) — smoke: invitation.scheduled-roundtrip-962 */
  shareMessageInput: 'dialog-share-message-input',
  /** The edit dialog's live count line under the recipient picker (#1549-b) */
  shareEditCount: 'dialog-share-edit-count',
  /** Recurring on/off IconButton (aria-pressed) — smoke: invitation.scheduled-roundtrip-962 */
  shareRecurringToggle: 'dialog-share-recurring-toggle',
  /** "Resend every N days" number input, rendered while Recurring is on — smoke: invitation.scheduled-roundtrip-962 */
  shareRecurrenceIntervalInput: 'dialog-share-recurrence-interval-input',
  /** "N selected from analytics" chip in select-all mode — smoke: share.invite-selection */
  shareAnalyticsCountChip: 'dialog-share-analytics-count-chip',
  /** Removable chip for an analytics-selected row in the bulk-invite dialog. */
  bulkInviteRemove: (rowId: number) => `bulk-invite-remove-${rowId}` as const,
  /** A muted (unreachable / completed) recipient chip's focusable wrapper, by row id (#1547). */
  bulkInviteMutedChip: (rowId: number) => `bulk-invite-muted-chip-${rowId}` as const,

  // Recipient picker actions
  recipientAddStatusButton: (status: string) => `dialog-recipient-add-status-${status}` as const,
  recipientAddLabelButton: (label: string) => `dialog-recipient-add-label-${label}` as const,
  recipientAttributeRow: (name: string, value: string) =>
    `dialog-recipient-attribute-row-${name}-${value}` as const,
  recipientAddAttributeButton: (name: string, value: string) =>
    `dialog-recipient-add-attribute-${name}-${value}` as const,
  recipientAddFilterButton: (id: number | string) => `dialog-recipient-add-filter-${id}` as const,
  recipientAddContactButton: (id: number | string) => `dialog-recipient-add-contact-${id}` as const,
  recipientContactRow: (id: number | string) => `dialog-recipient-contact-row-${id}` as const,
  /** Search input inside the recipient picker (#437). */
  recipientSearchInput: 'dialog-recipient-search-input',
  recipientAddButton: 'dialog-recipient-add-button',
  recipientAddEmailButton: (email: string) => `dialog-recipient-add-email-${email}` as const,
  recipientAddPhoneButton: (phone: string) => `dialog-recipient-add-phone-${phone}` as const,
  recipientAddNewEmailButton: 'dialog-recipient-add-new-email-button',
  recipientAddNewPhoneButton: 'dialog-recipient-add-new-phone-button',
  /**
   * A raw email/phone recipient chip, keyed by the stored value (#807b). A chip
   * the server refused carries `data-refused="true"` and is described by the
   * dialog's error band.
   */
  recipientChip: (value: string) => `dialog-recipient-chip-${value}` as const,

  // Change owner
  changeOwner: 'dialog-change-owner',
  changeOwnerSelect: 'dialog-change-owner-select',
  changeOwnerOption: (id: number) => `dialog-change-owner-option-${id}` as const,

  // Assign labels dialog
  assignLabels: 'dialog-assign-labels',
  assignLabelsSelectAllButton: 'dialog-assign-labels-select-all-button',
  assignLabelsEmptyIcon: 'dialog-assign-labels-empty-icon',

  // Manage labels
  manageLabels: 'dialog-manage-labels',
  manageLabelsSearch: 'dialog-manage-labels-search',
  manageLabelsSearchClearButton: 'dialog-manage-labels-search-clear-button',
  manageLabelsCreateNewChip: 'dialog-manage-labels-create-new-chip',
  manageLabelsSearchWarning: 'dialog-manage-labels-search-warning',
  manageLabelChip: (name: string) =>
    `dialog-manage-label-${name.replace(/[^\w.-]/g, (c) => `_${c.charCodeAt(0).toString(16)}_`)}` as const,
  manageLabelsNewLabelDeleteIcon: (name: string) => `dialog-manage-labels-new-${name.toLowerCase().replace(/\s+/g, '-')}-delete` as const,

  // Manage assignments
  manageAssignments: 'dialog-manage-assignments',
  manageAssignmentsCard: (action: string) => `manage-assignments-card-${action}` as const,
  manageAssignmentsConfirm: (action: string) => `manage-assignments-confirm-${action}` as const,
  manageAssignmentsCancel: (action: string) => `manage-assignments-cancel-${action}` as const,
  manageAssignmentsApply: (action: string) => `manage-assignments-apply-${action}` as const,

  // Export
  export: 'dialog-export',
  exportFormatSelect: 'dialog-export-format-select',
  exportSelectAllFieldsButton: 'dialog-export-select-all-fields-button',
  exportDownloadButton: 'dialog-export-download-button',

  // Image crop
  imageCrop: 'dialog-image-crop',
  imageCropBrowseButton: 'dialog-image-crop-browse-button',

  // Image picker (generic image upload dialog)
  imagePicker: {
    dialog: 'dialog-image-picker',
    dropzone: 'dialog-image-picker-dropzone',
    fileInput: 'dialog-image-picker-file-input',
    preview: 'dialog-image-picker-preview',
    removeButton: 'dialog-image-picker-remove-button',
    selectButton: 'dialog-image-picker-select-button',
    cancelButton: 'dialog-image-picker-cancel-button',
  },

  // Upload
  upload: 'dialog-upload',
  uploadDropzone: 'dialog-upload-dropzone',
  uploadDropzoneIcon: 'dialog-upload-dropzone-icon',
  uploadFileSelectedIcon: 'dialog-upload-file-selected-icon',
  uploadSuccessIcon: 'dialog-upload-success-icon',
  uploadFileInput: 'dialog-upload-file-input',
  uploadFileItem: (index: number) => `dialog-upload-file-${index}` as const,
  uploadSubmitButton: 'dialog-upload-submit-button',

  // QR Code
  qrCode: 'dialog-qr-code',
  qrCodeCloseButton: 'dialog-qr-code-close-button',
  qrCodeImage: 'dialog-qr-code-image',
  qrCodeDownloadButton: 'dialog-qr-code-download-button',

  // Copy link
  copyLink: 'dialog-copy-link',
  copyLinkCloseButton: 'dialog-copy-link-close-button',
  copyLinkInput: 'dialog-copy-link-input',
  copyLinkButton: 'dialog-copy-link-button',

  // Bulk share dialog
  bulkShare: {
    dialog: 'dialog-bulk-share',
    recipientPicker: 'dialog-bulk-share-recipient-picker',
    messageInput: 'dialog-bulk-share-message-input',
    contactMethodToggle: 'dialog-bulk-share-contact-method',
    schedulingHelper: 'dialog-bulk-share-scheduling-helper',
    startDatePicker: 'dialog-bulk-share-start-date',
    intervalToggle: 'dialog-bulk-share-interval',
    applyButton: 'dialog-bulk-share-apply-button',
    itemList: 'dialog-bulk-share-item-list',
    itemDatePicker: (shareId: number) => `dialog-bulk-share-item-${shareId}-date` as const,
    scheduleButton: 'dialog-bulk-share-schedule-button',
  },

  // Premium Content Library (staff only)
  premiumLibrary: {
    dialog: 'dialog-premium-library',
    searchInput: 'dialog-premium-library-search-input',
    contentGrid: 'dialog-premium-library-content-grid',
    selectionSummary: 'dialog-premium-library-selection-summary',
    selectionChip: (id: number) => `dialog-premium-library-selection-${id}` as const,
    clearSelection: 'dialog-premium-library-clear-selection',
  },
} as const;

// ============================================
// Common Form Elements
// ============================================

export const formTestIds = {
  // Inputs
  textInput: (name: string) => `form-input-${name}` as const,
  selectInput: (name: string) => `form-select-${name}` as const,
  checkboxInput: (name: string) => `form-checkbox-${name}` as const,
  radioInput: (name: string, value: string) => `form-radio-${name}-${value}` as const,
  dateInput: (name: string) => `form-date-${name}` as const,

  // File inputs
  fileInput: (name: string) => `form-file-${name}` as const,
  fileDropzone: (name: string) => `form-dropzone-${name}` as const,
  fileRemoveButton: (name: string) => `form-file-remove-${name}` as const,

  // Search input component
  searchClearButton: 'form-search-clear-button',

  // Avatar cropper
  avatarRemoveButton: 'form-avatar-remove-button',

  // Avatar editor
  avatarEditorUploadButton: 'form-avatar-editor-upload-button',
  avatarEditorDeleteButton: 'form-avatar-editor-delete-button',
  avatarEditorConfirmDeleteButton: 'form-avatar-editor-confirm-delete-button',
  avatarEditorCancelButton: 'form-avatar-editor-cancel-button',
  avatarEditorBackButton: 'form-avatar-editor-back-button',
  avatarEditorSaveButton: 'form-avatar-editor-save-button',
  avatarEditorEmptyPlaceholder: 'form-avatar-editor-empty-placeholder',
  /** Visible client-side upload rejection (extension / size). */
  avatarEditorUploadError: 'form-avatar-editor-upload-error',

  // Buttons
  submitButton: 'form-submit-button',
  resetButton: 'form-reset-button',
  cancelButton: 'form-cancel-button',

  // Validation
  errorMessage: (field: string) => `form-error-${field}` as const,
  helperText: (field: string) => `form-helper-${field}` as const,

  // Searchable list
  searchableList: {
    container: (prefix: string) => `${prefix}-list-container` as const,
    item: (prefix: string, id: number | string) => `${prefix}-list-item-${id}` as const,
  },
} as const;

// ============================================
// Common UI Elements
// ============================================

export const uiTestIds = {
  // Standard action buttons (used across features)
  // Use these for consistent action button testing
  actions: {
    // CRUD actions
    delete: 'action-delete',
    create: 'action-create',
    edit: 'action-edit',
    save: 'action-save',
    // List/selection actions
    archive: 'action-archive',
    unarchive: 'action-unarchive',
    copy: 'action-copy',
    merge: 'action-merge',
    move: 'action-move',
    swap: 'action-swap',
    // Organization actions
    labels: 'action-labels',
    owner: 'action-owner',
    user: 'action-user',
    share: 'action-share',
    filter: 'action-filter',
    sort: 'action-sort',
    // Navigation actions
    close: 'action-close',
    back: 'action-back',
    menu: 'action-menu',
    prev: 'action-prev',
    next: 'action-next',
    expand: 'action-expand',
    collapse: 'action-collapse',
    // Form actions
    cancel: 'action-cancel',
    confirm: 'action-confirm',
    submit: 'action-submit',
    clear: 'action-clear',
    add: 'action-add',
    remove: 'action-remove',
    // Utility actions
    download: 'action-download',
    certificate: 'action-certificate',
    upload: 'action-upload',
    refresh: 'action-refresh',
    retry: 'action-retry',
    search: 'action-search',
    preview: 'action-preview',
    history: 'action-history',
    revise: 'action-revise',
    grant: 'action-grant',
    premiumToggle: 'action-premium-toggle',
    // Analytics bulk actions
    invite: 'action-invite',
    complete: 'action-complete',
    unassign: 'action-unassign',
    resolve: 'action-resolve',
    manage: 'action-manage',
    update: 'action-update',
    // Image actions
    addImage: 'action-add-image',
    removeImage: 'action-remove-image',
    cropImage: 'action-crop-image',
    // Create purpose
    generate: 'action-generate',
    import: 'action-import',
    // Distribute purpose
    publish: 'action-publish',
    send: 'action-send',
    schedule: 'action-schedule',
    // Modify purpose
    recreate: 'action-recreate',
    attributes: 'action-attributes',
    // Destroy purpose
    reset: 'action-reset',
    disconnect: 'action-disconnect',
    deactivate: 'action-deactivate',
    // Restore purpose
    activate: 'action-activate',
    // Affirm purpose
    apply: 'action-apply',
    done: 'action-done',
    verify: 'action-verify',
    // Navigate purpose
    view: 'action-view',
    settings: 'action-settings',
    // Recover purpose
    resend: 'action-resend',
    // Authenticate purpose
    signIn: 'action-sign-in',
    signUp: 'action-sign-up',
    logOut: 'action-log-out',
    resetPassword: 'action-reset-password',
    // Select purpose
    selectAll: 'action-select-all',
    deselectAll: 'action-deselect-all',
    // Navigate purpose (additional)
    open: 'action-open',
    browse: 'action-browse',
    home: 'action-home',
    showSummary: 'action-show-summary',
    categorize: 'action-categorize',
  },

  // Loading states
  loadingState: 'ui-loading-state',
  loadingSpinner: 'ui-loading-spinner',
  loadingTryAgain: 'ui-loading-try-again',
  /** A legend row of a chart waiting skeleton (ChartSkeleton, DashboardChartSkeleton). */
  chartSkeletonLegendRow: 'ui-chart-skeleton-legend-row',
  loadingOverlay: 'ui-loading-overlay',
  skeleton: (name: string) => `ui-skeleton-${name}` as const,

  // Unreachable state (#239)
  unreachableState: {
    root: 'unreachable-state',
    retry: 'unreachable-state-retry',
  },

  // Navigation
  navbar: 'ui-navbar',
  sidebar: 'ui-sidebar',
  breadcrumb: 'ui-breadcrumb',
  tabs: 'ui-tabs',
  tab: (name: string) => `ui-tab-${name}` as const,

  // Alerts
  alert: (type: string) => `ui-alert-${type}` as const,
  alertDismiss: 'ui-alert-dismiss',

  // Pagination
  pagination: 'ui-pagination',
  paginationPrev: 'ui-pagination-prev',
  paginationNext: 'ui-pagination-next',
  paginationPage: (page: number) => `ui-pagination-page-${page}` as const,

  // Selection
  selectAllCheckbox: 'ui-select-all-checkbox',
  selectionCount: 'ui-selection-count',
  bulkActionsMenu: 'ui-bulk-actions-menu',

  // Search
  searchClearButton: 'ui-search-clear-button',

  // Empty states
  emptyState: 'ui-empty-state',
  emptyStateMessage: 'ui-empty-state-message',
  mobileEditorBlockedState: 'ui-mobile-editor-blocked-state',

  // Error states
  feedbackZone: {
    banner: 'feedback-zone-banner',
    item: (id: string) => `feedback-zone-item-${id}` as const,
    itemMessage: 'feedback-zone-item-message',
  },

  errorState: 'ui-error-state',
  errorStateIcon: 'ui-error-state-icon',
  errorStateMessage: 'ui-error-state-message',
  errorStateDescription: 'ui-error-state-description',
  errorStateRetryButton: 'ui-error-state-retry-button',
  errorStateAlert: 'ui-error-state-alert',
  errorStateInline: 'ui-error-state-inline',

  // PDF Viewer
  pdfViewer: 'ui-pdf-viewer',
  pdfViewerLoading: 'ui-pdf-viewer-loading',
  pdfPrevPageButton: 'ui-pdf-prev-page-button',
  pdfNextPageButton: 'ui-pdf-next-page-button',
  pdfZoomInButton: 'ui-pdf-zoom-in-button',
  pdfZoomOutButton: 'ui-pdf-zoom-out-button',
  pdfFullscreenButton: 'ui-pdf-fullscreen-button',

  // Subheader
  subheaderActionButton: 'ui-subheader-action-button',
  subheaderBackButton: 'ui-subheader-back-button',
  subheaderEditButton: 'ui-subheader-edit-button',
  subheaderHistoryButton: 'ui-subheader-history-button',
  subheaderStaffActions: 'ui-subheader-staff-actions',
  editorPublishConfirm: 'editor-publish-confirm',
  editorLeaveConfirm: 'editor-leave-confirm',
  quizQuestionDeleteConfirm: 'quiz-question-delete-confirm',
  componentDeleteConfirm: 'component-delete-confirm',
  checklistQuestionDeleteConfirm: 'checklist-question-delete-confirm',

  // InfoTip
  infoTipButton: 'ui-info-tip-button',

  // Filter
  filterResetButton: 'ui-filter-reset-button',
} as const;

// ============================================
// Charts
// ============================================

export const chartsTestIds = {
  choiceBreakdown: {
    container: 'chart-choice-breakdown',
    title: 'chart-choice-breakdown-title',
    questionText: 'chart-choice-breakdown-question',
    questionEdit: 'chart-choice-breakdown-question-edit',
    choiceEdit: (id: number | string) => `chart-choice-breakdown-choice-edit-${id}` as const,
    totalResponses: 'chart-choice-breakdown-total',
    emptyMessage: 'chart-choice-breakdown-empty',
    loadingState: 'chart-choice-breakdown-loading',
    // Empty-state placeholder bar tracks (by 1-based index)
    // Per-choice elements (by choiceId)
    choiceItem: (id: number | string) => `chart-choice-breakdown-item-${id}` as const,
    choiceLabel: (id: number | string) => `chart-choice-breakdown-label-${id}` as const,
    choiceCount: (id: number | string) => `chart-choice-breakdown-count-${id}` as const,
    choicePercentage: (id: number | string) => `chart-choice-breakdown-percentage-${id}` as const,
    choiceBar: (id: number | string) => `chart-choice-breakdown-bar-${id}` as const,
    choiceBarFill: (id: number | string) => `chart-choice-breakdown-bar-fill-${id}` as const,
    correctChip: (id: number | string) => `chart-choice-breakdown-correct-${id}` as const,
    flagChip: (id: number | string) => `chart-choice-breakdown-flag-${id}` as const,
    // 1524-c: change which choice is correct, in place
    keyOffer: (id: number | string) => `chart-choice-breakdown-key-offer-${id}` as const,
    keyPending: (id: number | string) => `chart-choice-breakdown-key-pending-${id}` as const,
    keyFailed: (id: number | string) => `chart-choice-breakdown-key-failed-${id}` as const,
    keyRetry: (id: number | string) => `chart-choice-breakdown-key-retry-${id}` as const,
  },
} as const;

// ============================================
// Analytics Tables
// ============================================

export const analyticsTestIds = {
  // Tables
  assignmentsTable: 'analytics-assignments-table',
  submissionsTable: 'analytics-submissions-table',

  // Rows
  /** The summary panel of a detail pane while it waits: the spinner's anchor (#1561 B1). */
  summaryPanelWaiting: 'analytics-summary-panel-waiting',
  tableRow: (id: number | string) => `analytics-row-${id}` as const,
  tableRowCheckbox: (id: number | string) => `analytics-row-${id}-checkbox` as const,
  /** #1059t: a ResponseTable row, keyed by the same id its getRowId returns (submission id in checklists). */
  responseRow: (id: number | string) => `analytics-response-row-${id}` as const,
  /** #1783: a ResponseTable row's text answer / chip list, keyed by the row's id. */
  responseAnswer: (id: number | string) => `analytics-response-answer-${id}` as const,
  /** #1783: one chosen-choice chip of a ResponseTable row (index in the answer's choices). */
  responseAnswerChip: (id: number | string, index: number) =>
    `analytics-response-answer-${id}-chip-${index}` as const,
  /** #1783: the ResponseTable's scroll container (its right edge is the pane's). */
  responseTable: 'analytics-response-table',

  // Cells
  orgCell: (id: number | string) => `analytics-org-cell-${id}` as const,
  /** #1059t: FlagStatusCell icons; not-flagged renders nothing. */
  flagStatusFlagged: 'flag-status-flagged',
  flagStatusResolved: 'flag-status-resolved',
  /** #875b: "can't be reached · …" caption under the contact name on the roster. */
  unreachableCaption: 'assignment-unreachable-caption',
  /** #875b: the same caption in AssignmentDetailDialog's Status detail. */
  unreachableCaptionDetail: 'assignment-detail-unreachable-caption',
  certificateCellButton: (id: number | string) => `analytics-certificate-cell-${id}` as const,
  certificateDownloadButton: 'analytics-certificate-download-button',
  certificateUploadButton: 'analytics-certificate-upload-button',
  certificateError: 'analytics-certificate-error',
  certificateFileInput: 'analytics-certificate-file-input',

  // Toolbar
  toolbar: 'analytics-toolbar',
  filterButton: 'analytics-filter-button',
  filterMenu: 'analytics-filter-menu',
  sortButton: 'analytics-sort-button',
  sortMenu: 'analytics-sort-menu',
  downloadButton: 'analytics-download-button',

  // Bulk actions
  bulkActionSend: 'analytics-bulk-send',
  bulkActionResend: 'analytics-bulk-resend',
  bulkActionRevoke: 'analytics-bulk-revoke',
  bulkActionResolve: 'analytics-bulk-resolve',
} as const;

// ============================================
// Shared Components
// ============================================

export const componentsTestIds = {
  // Action card (generic actionable list item)
  actionCard: {
    card: (id: string) => `action-card-${id}` as const,
    title: (id: string) => `action-card-${id}-title` as const,
    subtitle: (id: string) => `action-card-${id}-subtitle` as const,
    primaryAction: (id: string) => `action-card-${id}-primary` as const,
    overflowButton: (id: string) => `action-card-${id}-overflow` as const,
  },

  // Entity card
  entityCard: {
    container: (id: number | string) => `entity-card-${id}` as const,
    viewButton: (id: number | string) => `entity-card-${id}-view` as const,
    editButton: (id: number | string) => `entity-card-${id}-edit` as const,
    deleteButton: (id: number | string) => `entity-card-${id}-delete` as const,
    moreButton: (id: number | string) => `entity-card-${id}-more` as const,
    checkbox: (id: number | string) => `entity-card-${id}-checkbox` as const,
  },

  // Network mark (#1548): the grey network-icon square, or the logo + name, on cards and detail pages
  networkMark: {
    square: 'network-mark-square',
    logo: 'network-mark-logo',
    line: 'network-mark-line',
  },

  // Grid card (used by PacketsList, ChecklistsList)
  gridCard: {
    checkbox: 'grid-card-checkbox',
    /** Card root of a PacketCard — smoke: share.list-menu-407 scopes the checkbox to one card */
    packetCard: (id: number | string) => `packet-card-${id}` as const,
  },

  // Entity header
  entityHeader: {
    container: 'entity-header',
    clearSelectionButton: 'entity-header-clear-selection-button',
  },

  // Power table
  powerTable: {
    container: 'power-table',
    refreshButton: 'power-table-refresh-button',
  },

  // Drag drop list
  dragDropList: {
    container: 'drag-drop-list',
    item: (id: number | string) => `drag-drop-item-${id}` as const,
    /**
     * The dnd-kit drag activator. Exposed because reordering is only provable
     * end-to-end by driving the real sortable (the live staging smoke does it
     * with the keyboard sensor: focus handle -> Space -> Arrow -> Space).
     */
    handle: (id: number | string) => `drag-drop-item-${id}-handle` as const,
    /** Empty placeholder holding the handle's space on a locked row (#1419). */
    lockedSpacer: (id: number | string) => `drag-drop-item-${id}-locked-spacer` as const,
    deleteButton: (id: number | string) => `drag-drop-item-${id}-delete` as const,
  },

  // Collapsible sidebar
  collapsibleSidebar: {
    container: 'collapsible-sidebar',
    toggleButton: 'collapsible-sidebar-toggle-button',
  },

  // Filtered list layout
  filteredListLayout: {
    container: 'filtered-list-layout',
    resetFiltersButton: 'filtered-list-reset-filters-button',
  },

  // Share selector (#670b: multi-share chip row)
  shareSelector: {
    container: 'share-selector',
    chip: (shareId: number | string) => `share-selector-chip-${shareId}` as const,
    chipAll: 'share-selector-chip-all',
  },

  // Filter groups (tristate toggles and select dropdowns in filter bar)
  filterGroups: {
    triStateToggle: (name: string) => `filter-tristate-${name}` as const,
    selectGroup: (name: string) => `filter-select-${name}` as const,
  },

  // Filter navigation
  filterNavigation: {
    container: 'filter-navigation',
    expandButton: (section: string) => `filter-nav-expand-${section}` as const,
    collapseButton: (section: string) => `filter-nav-collapse-${section}` as const,
    categoryHeader: (id: string | number) => `filter-nav-category-${id}-header` as const,
    categoryEditButton: (id: string | number) => `filter-nav-category-${id}-edit` as const,
    categoryDeleteButton: (id: string | number) => `filter-nav-category-${id}-delete` as const,
    customFilterEditButton: (id: string | number) => `filter-nav-custom-filter-${id}-edit` as const,
    customFilterDeleteButton: (id: string | number) => `filter-nav-custom-filter-${id}-delete` as const,
    savedFiltersSettings: 'filter-nav-saved-filters-settings',
    manageSavedFiltersList: 'manage-saved-filters-list',
    /** A saved-filter row; clicking it toggles its selection (#1552). */
    manageSavedFilterRow: (id: string | number) => `manage-saved-filter-${id}-row` as const,
    /** Title-row actions on the selection (#1552). */
    manageSavedFilterRename: 'manage-saved-filters-rename',
    manageSavedFilterDelete: 'manage-saved-filters-delete',
    /** The footer's Delete, once the title-row Delete has asked (#1552). */
    manageSavedFilterConfirmDelete: 'manage-saved-filters-confirm-delete',
    manageSavedFilterInUseDialog: 'manage-saved-filter-in-use',
    manageSavedFilterInUseRow: (id: string | number) => `manage-saved-filter-in-use-row-${id}` as const,
  },

  // Dashboard section (viewer dashboard)
  dashboardSection: {
    expandButton: 'dashboard-section-expand-button',
    filterHintIcon: 'dashboard-section-filter-hint-icon',
  },

  // AppShell layout
  appShell: {
    userMenuTrigger: 'app-shell-user-menu-trigger',
    dropdownArrow: 'app-shell-dropdown-arrow',
    signInLink: 'app-shell-sign-in-link',
    /** The "Go to home" logo button — used by the 1208-s staging scenario */
    logo: 'app-shell-logo',
    /** A manager nav destination (desktop header icon or phone drawer row), by option name (#1400) */
    navIcon: (name: string) => `app-shell-nav-${name}` as const,
  },

  // Route error boundary
  routeErrorBoundary: {
    icon: 'route-error-boundary-icon',
  },

  // Failure screen (render crashes + 5xx)
  failureScreen: {
    root: 'failure-screen',
    traceId: 'failure-screen-trace-id',
    copyTrace: 'failure-screen-copy-trace',
    tryAgain: 'failure-screen-try-again',
    dashboard: 'failure-screen-dashboard',
  },

  // Contact methods list
  // F11: rename `container` to a distinct testid so it doesn't collide
  // with the top-level `contacts.methodsList`.
  contactMethodsList: {
    container: 'contact-methods-list-container',
    methodIcon: (method: string) => `contact-method-icon-${method.replace(/[^a-z0-9]/gi, '-').toLowerCase()}` as const,
    inactiveChip: (method: string) => `contact-method-inactive-chip-${method.replace(/[^a-z0-9]/gi, '-').toLowerCase()}` as const,
    badReason: (method: string) => `contact-method-bad-reason-${method.replace(/[^a-z0-9]/gi, '-').toLowerCase()}` as const,
    /** #969: the element holding ONLY the method's address text (every status row). */
    methodValue: (method: string) => `contact-method-value-${method.replace(/[^a-z0-9]/gi, '-').toLowerCase()}` as const,
    noActiveWarning: 'contact-methods-no-active-warning',
  },

  // Share settings controls (IconToggle, IconOption)
  shareSettings: {
    toggle: (name: string) => `share-settings-toggle-${name.toLowerCase().replace(/\s+/g, '-')}` as const,
    option: (name: string) => `share-settings-option-${name.toLowerCase().replace(/\s+/g, '-')}` as const,
    /** Read-only mode: the whole projected list. */
    readOnlyList: 'share-settings-read-only-list',
    /** Read-only mode: one projected setting row, keyed by `VisibleShareSetting.key`. */
    readOnlyRow: (key: string) => `share-settings-read-only-row-${key}` as const,
  },

  // Label picker
  labelPicker: {
    container: 'label-picker',
    searchClearButton: 'label-picker-search-clear-button',
  },

  // Questions (shared component)
  questions: {
    // Question editor
    editorImageUploadButton: 'question-editor-image-upload-button',
    editorImageRemoveButton: 'question-editor-image-remove-button',
    editorImagePreview: 'question-editor-image-preview',

    // Choice editor
    choiceImageUploadButton: (index: number) => `choice-${index}-image-upload-button` as const,
    choiceImageRemoveButton: (index: number) => `choice-${index}-image-remove-button` as const,
    choiceImagePreview: (index: number) => `choice-${index}-image-preview` as const,
    choiceFlagButton: (index: number) => `choice-${index}-flag-button` as const,

    // Questions manager
    managerAddQuestionButton: 'questions-manager-add-question-button',
    managerCloseButton: 'questions-manager-close-button',
    managerRowError: (index: number) => `questions-manager-row-${index}-error` as const,

    // Short answer settings
    shortAnswerToggleButton: (type: string) => `short-answer-toggle-${type}` as const,

    // Hotspot editor (region drawing)
    hotspotEditor: {
      container: 'hotspot-editor-container',
      canvas: 'hotspot-editor-canvas',
      region: (id: string) => `hotspot-region-${id}` as const,
      regionDeleteButton: (id: string) => `hotspot-region-${id}-delete` as const,
      regionToggleCorrect: (id: string) => `hotspot-region-${id}-toggle-correct` as const,
      addRegionHint: 'hotspot-add-region-hint',
      // RegionEditor toolbar
      selectTool: 'hotspot-select-tool',
      rectTool: 'hotspot-rect-tool',
      circleTool: 'hotspot-circle-tool',
      markCorrectButton: 'hotspot-mark-correct-button',
      deleteRegionButton: 'hotspot-delete-region-button',
    },

    // Hotspot display (consumer view)
    hotspotDisplay: {
      container: 'hotspot-display-container',
      image: 'hotspot-display-image',
      region: (id: string) => `hotspot-display-region-${id}` as const,
      selectedIndicator: 'hotspot-selected-indicator',
      feedbackOverlay: 'hotspot-feedback-overlay',
    },

    // Ordering editor
    orderingEditor: {
      container: 'ordering-editor-container',
      item: (index: number) => `ordering-editor-item-${index}` as const,
      addItemButton: 'ordering-editor-add-item-button',
    },

    // Ordering display (consumer view)
    orderingDisplay: {
      container: 'ordering-display-container',
      item: (id: number | string) => `ordering-display-item-${id}` as const,
      feedbackMessage: 'ordering-display-feedback',
    },

    // Matching editor
    matchingEditor: {
      container: 'matching-editor-container',
      pairRow: (index: number) => `matching-pair-${index}` as const,
      sourceInput: (index: number) => `matching-source-${index}` as const,
      targetInput: (index: number) => `matching-target-${index}` as const,
      addButton: 'matching-add-button',
      removeButton: (index: number) => `matching-remove-${index}` as const,
    },

    // Matching display (consumer view)
    matchingDisplay: {
      container: 'matching-display-container',
      sourceItem: (index: number) => `matching-source-item-${index}` as const,
      targetItem: (index: number) => `matching-target-item-${index}` as const,
      connector: (sourceId: string) => `matching-connector-${sourceId}` as const,
      feedbackOverlay: 'matching-feedback-overlay',
    },
  },

  // Account-merge dialog (proactive + 403 denial variants)
  accountMerge: {
    dialog: 'account-merge-dialog',
    methodCard: 'account-merge-method-card',
    ignoreButton: 'account-merge-ignore-button',
    goBackButton: 'account-merge-go-back-button',
    verifyButton: 'account-merge-verify-button',
    cancelButton: 'account-merge-cancel-button',
    codeInput: 'account-merge-code-input',
  },

  // Cross-user contact-method claim (identity-plan.md §7)
  contactMethodClaim: {
    dialog: 'contact-method-claim-dialog',
    claimButton: 'contact-method-claim-send-code-button',
    verifyButton: 'contact-method-claim-verify-button',
    cancelButton: 'contact-method-claim-cancel-button',
    codeInput: 'contact-method-claim-code-input',
  },

  // Media loading state
  mediaLoadingState: {
    icon: 'media-loading-state-icon',
    /** No file attached at all — distinct from processing and from failed. */
    empty: 'media-loading-state-empty',
  },

  // User menu
  userMenu: {
    item: (key: string) => `user-menu-item-${key}` as const,
  },

  // Subheader
  subheader: {
    icon: 'subheader-icon',
    /** The green action bar (right section) of the Subheader; the green-bars guard reads it. */
    bar: 'subheader-bar',
    toggleLeftPaneButton: 'subheader-toggle-left-pane-button',
    toggleRightPaneButton: 'subheader-toggle-right-pane-button',
    /** Share trigger button — smoke: 1086-s */
    shareTrigger: 'subheader-share-trigger',
    /** Share > Invite menu item — smoke: share.invite-selection */
    shareMenuInvite: 'subheader-share-menu-invite',
    /** Share > Link menu item — smoke: share.invite-selection */
    shareMenuLink: 'subheader-share-menu-link',
    /** Share > QR Code menu item — smoke: share.invite-selection */
    shareMenuQrCode: 'subheader-share-menu-qrcode',
  },

  // Save status indicator (editor subheader) — always rendered, `data-state` = SaveStatus.
  // The 1208-s staging scenario waits on it instead of the ~2 s "Saved" fade.
  saveStatusIndicator: {
    root: 'editor-save-status',
  },
} as const;

// ============================================
// Upload Queue
// ============================================

export const uploadQueueTestIds = {
  container: 'upload-queue',
  item: (id: string) => `upload-queue-item-${id}` as const,
  itemRetryButton: (id: string) => `upload-queue-item-${id}-retry` as const,
  itemCancelButton: (id: string) => `upload-queue-item-${id}-cancel` as const,
  itemRemoveButton: (id: string) => `upload-queue-item-${id}-remove` as const,
} as const;

// ============================================
// Files (FileRef presentation — <FileAttachment>)
// ============================================

export const filesTestIds = {
  attachment: 'file-attachment',
  image: 'file-attachment-image',
  thumbnail: 'file-attachment-thumbnail',
  thumbnailIcon: 'file-attachment-thumbnail-icon',
  processing: 'file-attachment-processing',
  failed: 'file-attachment-failed',
  retryButton: 'file-attachment-retry',
  removeButton: 'file-attachment-remove',
  download: 'file-attachment-download',
} as const;

// ============================================
// Imports (SCORM, etc.)
// ============================================

export const importsTestIds = {
  // SCORM Import Dialog
  scormDialog: 'scorm-import-dialog',
  scormDropzone: 'scorm-import-dropzone',
  scormFileInput: 'scorm-import-file-input',
  scormBrowseButton: 'scorm-import-browse-button',
  scormSelectedFile: 'scorm-import-selected-file',
  scormRemoveFileButton: 'scorm-import-remove-file-button',
  scormUploadButton: 'scorm-import-upload-button',
  scormCancelButton: 'scorm-import-cancel-button',
  scormCloseButton: 'scorm-import-close-button',
  scormProgressBar: 'scorm-import-progress-bar',
  scormSuccessMessage: 'scorm-import-success-message',
  scormErrorMessage: 'scorm-import-error-message',
  scormWarningsList: 'scorm-import-warnings-list',
  scormSkippedList: 'scorm-import-skipped-list',
  scormViewPacketButton: 'scorm-import-view-packet-button',

  // Assignment Import Dialog (packet detail → status bar Import)
  assignmentsAction: 'assignments-import-action',
  assignmentsDialog: 'assignments-import-dialog',
  assignmentsShareSelect: 'assignments-import-share-select',
  assignmentsShareOption: (shareId: number) => `assignments-import-share-option-${shareId}` as const,
  assignmentsInactiveNotice: 'assignments-import-inactive-notice',
  assignmentsColumnsHint: 'assignments-import-columns-hint',
  assignmentsDropzone: 'assignments-import-dropzone',
  assignmentsFileInput: 'assignments-import-file-input',
  assignmentsBrowseButton: 'assignments-import-browse-button',
  assignmentsSelectedFile: 'assignments-import-selected-file',
  assignmentsRemoveFileButton: 'assignments-import-remove-file-button',
  assignmentsUploadButton: 'assignments-import-upload-button',
  assignmentsCancelButton: 'assignments-import-cancel-button',
  assignmentsDoneButton: 'assignments-import-done-button',
  assignmentsFileError: 'assignments-import-file-error',
  assignmentsSuccessMessage: 'assignments-import-success-message',
} as const;

// ============================================
// Test Utilities
// ============================================

export const testUtilsTestIds = {
  // renderWithProviders helper
  locationDisplay: 'test-location-display',
} as const;

// ============================================
// Staff Management
// ============================================

export const staffManagementTestIds = {
  // Navigation and page
  navIcon: 'staff-management-nav-icon',
  page: 'staff-management-page',

  // Create org/network dropdown (subheader)
  createMenuTrigger: 'staff-create-menu-trigger',
  createOrgMenuItem: 'staff-create-org-menu-item',
  createNetworkMenuItem: 'staff-create-network-menu-item',

  // Network management
  networkDetails: 'network-details-section',
  networkAccess: 'network-access-section',
  networkOrgsSection: 'network-orgs-section',
  networkOrgsTable: 'network-orgs-table',
  networkUsersSection: 'network-users-section',
  networkUsersTable: 'network-users-table',

  // Organization management
  orgDetails: 'org-details-section',
  orgAccess: 'org-access-section',
  orgSettings: 'org-settings-section',
  orgUsersSection: 'org-users-section',
  orgUsersTable: 'org-users-table',

  // Buttons
  addOrgButton: 'add-org-button',
  addUserButton: 'add-user-button',
  createNetworkButton: 'create-network-button',
  createOrgButton: 'create-org-button',

  // Dialogs
  createNetworkDialog: 'create-network-dialog',
  createOrgDialog: 'create-org-dialog',
  addOrgDialog: 'add-org-dialog',
  addUserDialog: 'add-user-dialog',
  editSeatsDialog: 'edit-seats-dialog',
  editUserDialog: 'edit-user-dialog',
  mergeUsersDialog: 'merge-users-dialog',
  confirmRemoveDialog: 'confirm-remove-dialog',
  confirmDeactivateDialog: 'confirm-deactivate-dialog',

  // Table rows and actions
  orgRow: (id: number) => `staff-org-row-${id}` as const,
  userRow: (id: number) => `staff-user-row-${id}` as const,
  editSeatsButton: (id: number) => `staff-edit-seats-${id}` as const,
  removeOrgButton: (id: number) => `staff-remove-org-${id}` as const,
  editUserButton: (id: number) => `staff-edit-user-${id}` as const,
  removeUserButton: (id: number) => `staff-remove-user-${id}` as const,
  deactivateUserButton: (id: number) => `staff-deactivate-user-${id}` as const,
  reactivateUserButton: (id: number) => `staff-reactivate-user-${id}` as const,
  mergeUserButton: (id: number) => `staff-merge-user-${id}` as const,

  // Merge users dialog (#223 chunk 2)
  mergeDiff: 'staff-merge-diff',
  mergePrimaryCard: 'staff-merge-primary-card',
  mergeDuplicateCard: 'staff-merge-duplicate-card',
  mergeSwapButton: 'staff-merge-swap',
  mergeConfirmInput: 'staff-merge-confirm-input',

  // Search results
  searchResult: (id: number) => `staff-search-result-${id}` as const,

  // Form fields
  nameInput: 'staff-name-input',
  identifierInput: 'staff-identifier-input',
  seatsInput: 'staff-seats-input',
  roleSelect: 'staff-role-select',
  levelSelect: 'staff-level-select',
  canEditLogoCheckbox: 'staff-can-edit-logo-checkbox',
  firstNameInput: 'staff-first-name-input',
  lastNameInput: 'staff-last-name-input',
  emailInput: 'staff-email-input',

  // Toggles (access flags)
  accessToggle: (flag: string) => `staff-access-toggle-${flag}` as const,
  settingInput: (setting: string) => `staff-setting-${setting}` as const,
  /** "Inherited from …" line on an org setting whose value comes from the network/default. */
  settingInherited: (setting: string) => `staff-setting-${setting}-inherited` as const,
  /** "Reset to inherited" action on an org setting that overrides the network. */
  settingReset: (setting: string) => `staff-setting-${setting}-reset` as const,
  /** Network-pane warning that a setting applies to every non-overriding member org. */
  settingNetworkWarning: (setting: string) => `staff-setting-${setting}-network-warning` as const,
} as const;

// ============================================
// Staff (Report Latest)
// ============================================

export const staffTestIds = {
  reportLatest: {
    page: 'report-latest-page',
    status: 'report-latest-status',
    retry: 'report-latest-retry',
    backToReports: 'report-latest-back-to-reports',
  },
} as const;

// ============================================
// Admin (Superuser Tools)
// ============================================

export const adminTestIds = {
  // Main container
  container: 'admin-container',

  // Work Queue tool
  workQueue: {
    shell: 'work-queue-shell',
    summary: 'wq-summary',
    alertsAllGood: 'wq-alerts-all-good',
    tabQueue: 'wq-tab-queue',
    tabHistory: 'wq-tab-history',
    queueSidebar: 'wq-queue-sidebar',
    historySidebar: 'wq-history-sidebar',
    summaryWorkers: 'wq-summary-workers',
    queueSearch: 'wq-queue-search',
    historySearch: 'wq-history-search',
    windowSelector: 'wq-window-selector',
    queueDetailDialog: 'wq-queue-detail-dialog',
    historyDetailDialog: 'wq-history-detail-dialog',
    queueDetailDelete: 'wq-queue-detail-delete',
    historyDetailDelete: 'wq-history-detail-delete',
    historyDetailRetry: 'wq-history-detail-retry',
    historyBulkDelete: 'wq-history-bulk-delete',
  },

  // Alerts tool
  alerts: {
    container: 'alerts-container',
    breadcrumb: 'alerts-breadcrumb',
    severityFilter: 'alerts-severity-filter',
    statusFilter: 'alerts-status-filter',
    statusFilterOption: (status: string) => `alerts-status-filter-${status}` as const,
    severityFilterOption: (severity: string) => `alerts-severity-filter-${severity}` as const,
    categoryFilter: 'alerts-category-filter',
    refreshButton: 'alerts-refresh-button',
    conditionsTable: 'alerts-conditions-table',
    conditionRow: (id: number) => `alerts-condition-row-${id}` as const,
    conditionManageButton: (id: number) => `alerts-condition-manage-${id}` as const,
    conditionActionsDialog: 'alerts-condition-actions-dialog',
    conditionAcknowledge: 'alerts-condition-acknowledge',
    conditionResolve: 'alerts-condition-resolve',
    conditionDisposition: 'alerts-condition-disposition',
    conditionNotes: 'alerts-condition-notes',
    conditionIssueUrlInput: 'alerts-condition-issue-url-input',
    conditionResolvedByInput: 'alerts-condition-resolved-by-input',
    conditionNotesInput: 'alerts-condition-notes-input',
    conditionDispositionSelect: 'alerts-condition-disposition-select',
    conditionLifecycleConfirm: 'alerts-condition-lifecycle-confirm',
    occurrenceTable: 'alerts-occurrence-table',
    occurrenceDeleteConfirm: 'alerts-occurrence-delete-confirm',
    row: (id: string) => `alerts-row-${id}` as const,
    detailDialog: 'alerts-detail-dialog',
    deleteButton: 'alerts-delete-button',
    contextViewer: 'alerts-context-viewer',
  },

  // Consume Logs tool
  consumeLogs: {
    container: 'consume-logs-container',
    table: 'consume-logs-table',
    row: (shareStub: string, sessionKey: string) =>
      `consume-logs-row-${shareStub}-${sessionKey}` as const,
    searchInput: 'consume-logs-search',
    errorsFilter: 'consume-logs-errors-filter',
    sessionDialog: 'consume-session-dialog',
    tripletViewer: 'triplet-viewer',
    panel: (name: string) => `triplet-panel-${name}` as const,
    navPrev: 'triplet-nav-prev',
    navNext: 'triplet-nav-next',
    syncPath: 'triplet-sync-path',
  },

  // Reports tool
  reports: {
    tool: 'admin-reports-tool',
    row: (key: string) => `admin-reports-row-${key}` as const,
    enabledToggle: (key: string) => `admin-reports-enabled-toggle-${key}` as const,
    intervalInput: (key: string) => `admin-reports-interval-input-${key}` as const,
    intervalSave: (key: string) => `admin-reports-interval-save-${key}` as const,
    regenerate: (key: string) => `admin-reports-regenerate-${key}` as const,
    open: (key: string) => `admin-reports-open-${key}` as const,
    status: (key: string) => `admin-reports-status-${key}` as const,
    lastGenerated: (key: string) => `admin-reports-last-generated-${key}` as const,
    nextDue: (key: string) => `admin-reports-next-due-${key}` as const,
    error: (key: string) => `admin-reports-error-${key}` as const,
  },

  // Trace lookup
  trace: {
    searchInput: 'trace-search-input',
    searchButton: 'trace-search-button',
    dialog: 'trace-lookup-dialog',
    timeline: 'trace-timeline',
    chip: 'trace-id-chip',
  },
} as const;

// ============================================
// Combined Export
// ============================================

/**
 * Main test IDs export - use this in components and tests
 *
 * @example
 * // In a component
 * <Button data-testid={testIds.contacts.deleteButton}>Delete</Button>
 *
 * // In a test
 * screen.getByTestId(testIds.contacts.deleteButton);
 */
// ============================================
// Integrations Feature
// ============================================

export const integrationsTestIds = {
  page: 'integrations-page',
  card: (sourceType: string) => `integration-card-${sourceType}` as const,
  statusBadge: (sourceType: string) => `integration-status-${sourceType}` as const,
  syncButton: 'integration-sync-button',
  editMappingsButton: 'integration-edit-mappings-button',
  disconnectButton: 'integration-disconnect-button',
  syncProgress: 'integration-sync-progress',
  syncError: 'integration-sync-error',
  errorDetailDialog: 'integration-error-detail-dialog',
  mappingSummary: 'integration-mapping-summary',
  disconnectDialog: 'integration-disconnect-dialog',

  // Manage-tab master/detail
  overview: 'integration-overview',
  overviewRow: (sourceType: string) => `integration-overview-row-${sourceType}` as const,
  detailEmpty: 'integration-detail-empty',
  notConnected: 'integration-not-connected',

  // Mapping workspace
  workspace: 'mapping-workspace',
  previewCards: 'mapping-preview-cards',
  previewCard: (index: number) => `mapping-preview-${index}` as const,
  mockIndicator: 'mapping-mock-indicator',
  saveButton: 'mapping-save-button',
  cancelButton: 'mapping-cancel-button',
  backButton: 'mapping-back-button',

  // Data browser
  dataBrowser: 'mapping-data-browser',
  sampleRecord: (index: number) => `mapping-sample-record-${index}` as const,
  dataNode: (path: string) => `mapping-data-node-${path}` as const,
  leafValue: (path: string) => `mapping-leaf-${path}` as const,
  tagBadge: (path: string) => `mapping-tag-${path}` as const,

  // Tag popover
  tagPopover: 'mapping-tag-popover',
  tagTargetSelect: 'mapping-tag-target-select',
  tagNameInput: 'mapping-tag-name-input',
  tagRemoveButton: 'mapping-tag-remove',

  // Import summary
  importSummary: 'mapping-import-summary',
  summaryGroup: (target: string) => `mapping-summary-group-${target}` as const,
  summaryMapping: (path: string) => `mapping-summary-${path}` as const,
} as const;

// ============================================
// Viewer Home
// ============================================

export const viewerHomeTestIds = {
  certificatesContainer: 'certificates-view',
  certificatesTile: (id: number | string) => `certificates-tile-${id}` as const,
  certificatesSearch: 'certificates-search',
  certificatesLoadMoreSentinel: 'certificates-load-more-sentinel',
  libraryContainer: 'library-view',
  libraryTile: (id: number | string) => `library-tile-${id}` as const,
  librarySearch: 'library-search',
  libraryLoadMoreSentinel: 'library-load-more-sentinel',
} as const;

// ============================================
// Viewer Dashboard (To Do / Done list at /dashboard)
// ============================================

export const viewerDashboardTestIds = {
  container: 'viewer-dashboard',
  search: 'viewer-dashboard-search',
  certificatesLink: 'viewer-dashboard-certificates-link',
  libraryLink: 'viewer-dashboard-library-link',
  addMethod: 'viewer-dashboard-add-method',
  noAssignments: 'viewer-dashboard-no-assignments',
  searchClear: 'viewer-dashboard-search-clear',
  tab: (value: 'todo' | 'done') => `viewer-dashboard-tab-${value}` as const,
  typeFilter: (value: 'all' | 'packet' | 'checklist') =>
    `viewer-dashboard-type-${value}` as const,
  empty: (value: 'todo' | 'done') => `viewer-dashboard-empty-${value}` as const,
  card: (id: number | string) => `viewer-dashboard-card-${id}` as const,
  cardTitle: (id: number | string) => `viewer-dashboard-card-${id}-title` as const,
  cardCertificate: (id: number | string) => `viewer-dashboard-card-${id}-certificate` as const,
  cardLastInvited: (id: number | string) => `viewer-dashboard-card-${id}-invited` as const,
  cardProgress: (id: number | string) => `viewer-dashboard-card-${id}-progress` as const,
  cardMessagesToggle: (id: number | string) => `viewer-dashboard-card-${id}-messages-toggle` as const,
  cardMessagesThread: (id: number | string) => `viewer-dashboard-card-${id}-messages-thread` as const,
  loadMoreSentinel: 'viewer-dashboard-load-more',
} as const;

// ============================================
// Content (shared packet/checklist)
// ============================================

export const contentTestIds = {
  /** #376: banner shown on a superseded revision's detail page. */
  supersededBanner: 'content-superseded-banner',
  supersededLink: 'content-superseded-link',
  /** vingreact#2065: the banner for a link with no active version, and its action. */
  noActiveVersionBanner: 'content-no-active-version-banner',
  noActiveVersionAction: 'content-no-active-version-action',
} as const;

// ============================================
// Failed files (#1534)
// ============================================

// Failed-file banner (#1534): one line per failed file with its own Replace, shared by the packet
// and (a2) checklist detail pages. `banner` / `imageBanner` keep the ids the staging smoke reads.
export const failedFilesTestIds = {
  banner: 'failed-file-banner',
  imageBanner: 'failed-image-banner',
  region: 'failed-files-region',
  heading: 'failed-files-heading',
  line: (id: number | string) => `failed-files-line-${id}` as const,
  replace: (id: number | string) => `failed-files-replace-${id}` as const,
  input: (id: number | string) => `failed-files-input-${id}` as const,
  adminLine: 'failed-files-admin-line',
  // The row's error icon (a button with permission) and its own picker input.
  rowIcon: (id: number | string) => `component-error-icon-${id}` as const,
  rowInput: (id: number | string) => `failed-files-row-input-${id}` as const,
  // The 'Files are being processed' strip under the banner.
  processingStrip: 'files-processing-strip',
} as const;

export const testIds = {
  auth: authTestIds,
  contacts: contactsTestIds,
  packets: packetsTestIds,
  checklists: checklistsTestIds,
  content: contentTestIds,
  consume: consumeTestIds,
  dashboard: dashboardTestIds,
  profile: profileTestIds,
  dialog: dialogTestIds,
  form: formTestIds,
  ui: uiTestIds,
  charts: chartsTestIds,
  analytics: analyticsTestIds,
  components: componentsTestIds,
  uploadQueue: uploadQueueTestIds,
  files: filesTestIds,
  imports: importsTestIds,
  testUtils: testUtilsTestIds,
  contextSwitcher: contextSwitcherTestIds,
  contextSettings: contextSettingsTestIds,
  contextRequired: contextRequiredTestIds,
  forcedSelection: forcedSelectionTestIds,
  ctxNoAccess: ctxNoAccessTestIds,
  staffManagement: staffManagementTestIds,
  staff: staffTestIds,
  admin: adminTestIds,
  integrations: integrationsTestIds,
  viewerHome: viewerHomeTestIds,
  viewerDashboard: viewerDashboardTestIds,
  failedFiles: failedFilesTestIds,
} as const;

// ============================================
// Type Helpers
// ============================================

/** All static test ID values */
export type TestId = string;

/** Helper to get test ID selector for querySelector */
export function testIdSelector(id: TestId): string {
  return `[data-testid="${id}"]`;
}

/** Helper to get all elements with test IDs matching a pattern */
export function testIdPattern(pattern: string): string {
  return `[data-testid^="${pattern}"]`;
}
